import type { ElementType } from 'react';
import { useIxmUserauthV1RealmQueryServiceRealmGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-realm-query-service-realm-get';
import { DEFAULT_PILL_ELEMENT, type DefaultPillElement, Pill, type PillProps, type PillVariant } from '@interxfi/design-system/components/pill/pill';
import type { IxmUserauthV1JoinedRealmAccess } from '@interxfi/types';
import { getRealmTypeForRealmName, type RealmType } from './realm-utils';

const variantByRealmType: Record<RealmType, PillVariant> = {
  sandbox: 'secondary',
  live: 'green',
  integrated: 'primary',
  unknown: 'neutral',
};

type RealmPillProps<AsComponent extends ElementType = DefaultPillElement> = PillProps<AsComponent> & {
  realmName: string | undefined;
};

export function RealmPill<AsComponent extends ElementType = DefaultPillElement>({ as, children, realmName, ...rest }: RealmPillProps<AsComponent>) {
  const Component = (as ?? DEFAULT_PILL_ELEMENT) satisfies ElementType;

  return (
    <Pill as={Component} variant={variantByRealmType[getRealmTypeForRealmName(realmName)]} {...rest}>
      {children || realmName}
    </Pill>
  );
}

type RealmPillFromIdProps<AsComponent extends ElementType = DefaultPillElement> = PillProps<AsComponent> & {
  realmId: string | undefined;
  withAccess?: IxmUserauthV1JoinedRealmAccess;
};

export function RealmPillFromId<AsComponent extends ElementType = DefaultPillElement>({
  as,
  children,
  isPending: pillPending,
  realmId,
  withAccess,
  ...rest
}: RealmPillFromIdProps<AsComponent>) {
  const { data, isPending, error } = useIxmUserauthV1RealmQueryServiceRealmGet(realmId ? { realmId } : undefined, undefined, withAccess);
  const showPending = Boolean(isPending || error);

  const Component = (as ?? DEFAULT_PILL_ELEMENT) satisfies ElementType;
  const realmName = data?.realm?.data?.spec?.name;

  return (
    <Pill as={Component} isPending={pillPending || showPending} variant={variantByRealmType[getRealmTypeForRealmName(realmName)]} {...rest}>
      {children || realmName}
    </Pill>
  );
}
